import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} Natysports: 0.5 → 0.7 so our taller summary (Check out + Continue shopping) stays pinned on phones. */
  #summaryThreshold = 0.7;

  /* Natysports: re-measure when the drawer or summary changes size (Safari's toolbar, the installments line
     being rewritten, express buttons loading). Horizon only measured once, a frame after add-to-cart, so an
     auto-opened drawer could get stuck with Check out scrolled out of view until it was reopened. */
  #resizeObserver = new ResizeObserver(() => {
    if (this.#themeDrawer?.isOpen) this.#updateStickyState();
  });

  /** @type {Element | null} */
  #observedSummary = null;

  /* Natysports: the first add fills an empty drawer after the measurement above has already run (and found no
     summary, so it unpinned), so also re-measure whenever the drawer's contents change. */
  #contentFrame = 0;
  #mutationObserver = new MutationObserver(() => {
    cancelAnimationFrame(this.#contentFrame);
    this.#contentFrame = requestAnimationFrame(() => this.#updateStickyState());
  });

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.#resizeObserver.disconnect();
    this.#observedSummary = null;
    this.#mutationObserver.disconnect();
    cancelAnimationFrame(this.#contentFrame);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Natysports: Horizon opened a non-empty cart right away, covering the Add to bag checkmark. Always wait for
    // the add to finish (below), as Horizon already did for an empty cart, so the checkmark plays every time.

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          await this.#contentReady();
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Natysports: resolves once the new cart contents are in place and their photos are loaded (at most 700ms), so
   * the drawer slides out already filled instead of the new item popping in mid-slide.
   */
  async #contentReady() {
    // The cart items re-render in their own handler for the same event; two frames lets it finish.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Lazy photos in a closed drawer only start loading once it opens, so load them now.
    const images = [...this.querySelectorAll('.cart-drawer__items img')].filter(
      (img) => img instanceof HTMLImageElement && !img.complete
    );
    if (!images.length) return;
    for (const img of images) if (img instanceof HTMLImageElement) img.loading = 'eager';
    await Promise.race([
      Promise.all(images.map((img) => img instanceof HTMLImageElement && img.decode().catch(() => {}))),
      new Promise((resolve) => setTimeout(resolve, 700)),
    ]);
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Cart re-renders replace the summary element, so re-point the observer when it changes. Only then:
    // observe() fires the callback again, which would loop.
    if (this.#observedSummary !== summary) {
      this.#observedSummary = summary;
      this.#resizeObserver.disconnect();
      this.#resizeObserver.observe(dialog);
      this.#resizeObserver.observe(summary);
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    // Natysports: a closed drawer measures 0 tall; keep the last state and measure again on open.
    if (!drawerHeight) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
